import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { X, ArrowLeft, ArrowRight, PlayCircle, Flag, Bot, Hammer, BookOpen, Users, Lightbulb, GraduationCap } from "lucide-react";

export const startDemoTour = () => window.dispatchEvent(new Event("demo-tour-start"));

const MAESTRO_ICONS = { Build: Hammer, Understand: BookOpen, Coordinate: Users, Advise: Lightbulb, Learn: GraduationCap };

const MAESTRO_STEPS = [
  { route: "/home", maestro: true, intro: true, title: "Meet Maestro", kicker: "Your AI Project Conductor",
    text: "Maestro isn't another chatbot. Maestro understands your projects, your portfolio, your organization's knowledge, and the people delivering the work.",
    hint: "That's Maestro — the glowing button at the top right. Here's what it can actually do." },
  { route: "/home", maestro: true, title: "Build", quote: "I'll create what doesn't exist.",
    items: ["Project Charter", "RAID Log", "Test Plan", "Steering Committee Deck"] },
  { route: "/home", maestro: true, title: "Understand", quote: "I'll read what already exists.",
    items: ["SOWs", "Meeting transcripts", "Emails", "Vendor proposals", "Requirements"] },
  { route: "/home", maestro: true, title: "Coordinate", quote: "I'll keep everyone moving together.",
    items: ["Cards", "Follow-ups", "Meeting prep", "Status updates", "True-Ups"] },
  { route: "/home", maestro: true, title: "Advise", quote: "I'll help you make better decisions.",
    items: ["Risks", "Dependencies", "Capacity", "Resources", "Trade-offs"] },
  { route: "/home", maestro: true, title: "Learn", quote: "I'll make every project improve the next.",
    text: "That's the PMO intelligence layer. Every project archived makes the whole organization smarter — and Maestro puts that knowledge to work. Now let's see the platform it conducts." },
];

const buildSteps = (canPMO) => [
  ...MAESTRO_STEPS,
  { route: "/home", title: "My Dashboard", text: "Your week at a glance — task-progress donuts with week-over-week trends, due readouts and the meeting strip. Meeting colors flag priority (red = critical)." },
  { route: "/portfolio", title: "Portfolio", text: "Executive roll-up of every PM's projects. Try 'Group by PM' — health, risks and late actions aggregate automatically. David's Data Center Migration is off-track; Maya's replatform is healthy." },
  ...(canPMO ? [
    { route: "/assignments", title: "Project Assignments", text: "Supervisors assign projects to PMs — ownership transfers to their workspace and they're notified by email. Resources are staffed straight from the registry." },
    { route: "/pmo", title: "PMO Dashboard", text: "The organizational intelligence layer — archived knowledge, AI search and resource intelligence. Every stat tile and row drills into the underlying data." },
    { route: "/pmo/registry", title: "Auto Pilot velocity", text: "Auto Pilot measures real delivery pace. Sarah Kim was downgraded to Slow (her ERP build ran at 20% of plan); Raj Patel was promoted to Fast. Click a velocity badge to see exactly why." },
    { route: "/pmo/ai-plan", title: "AI Plan", text: "Generate a best-practice plan with AI — schedules calibrate to measured velocity: Slow resources get longer durations, Fast get shorter, and Auto Pilot shows its reasoning." },
    { route: "/audit", title: "Audit", text: "Every PM scored against delivery standards. Drill into any project's hygiene checklist — project-level audit also lives under Track in the project menu." },
  ] : []),
  { route: "/lessons", title: "Lessons Learned", text: "An industry-standard lessons register with one-click AI assessments. Archiving a project auto-generates its assessment into the PMO knowledge base — the organization gets smarter with every project." },
];

export default function DemoTour() {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const nav = useNavigate();
  const { user } = useAuth();
  const canPMO = ["supervisor", "admin"].includes(user?.role);
  const steps = buildSteps(canPMO);

  useEffect(() => {
    const start = () => { setStep(0); setActive(true); nav(buildSteps(canPMO)[0].route); };
    window.addEventListener("demo-tour-start", start);
    return () => window.removeEventListener("demo-tour-start", start);
  }, [nav, canPMO]);

  const s = steps[Math.min(step, steps.length - 1)];

  useEffect(() => {
    const on = active && !!s?.maestro;
    document.body.classList.toggle("tour-maestro-spotlight", on);
    return () => document.body.classList.remove("tour-maestro-spotlight");
  }, [active, s]);

  if (!active || !user) return null;
  const last = step >= steps.length - 1;
  const go = (i) => { setStep(i); nav(steps[i].route); };
  const Icon = s.maestro && !s.intro ? MAESTRO_ICONS[s.title] : null;

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[80] w-[min(560px,calc(100vw-2rem))]" data-testid="demo-tour-card">
      <div className={`rounded-2xl border text-white shadow-2xl p-4 ${s.maestro ? "border-amber-600/60 bg-slate-950 shadow-amber-900/30" : "border-slate-700 bg-slate-900"}`}>
        <div className="flex items-start justify-between gap-3">
          <p className={`text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${s.maestro ? "text-amber-400" : "text-sky-400"}`}>
            {s.maestro ? <Bot className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
            {s.maestro ? "Maestro" : "Demo tour"} · {step + 1} / {steps.length}
          </p>
          <button onClick={() => setActive(false)} className="text-slate-400 hover:text-white transition-colors" title="End tour" data-testid="demo-tour-exit">
            <X className="h-4 w-4" />
          </button>
        </div>

        {s.intro ? (
          <div className="mt-1">
            <p className="font-display text-2xl font-extrabold tracking-tight bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent" data-testid="demo-tour-step-title">{s.title}</p>
            <p className="text-xs font-bold uppercase tracking-widest text-amber-500/90 mt-0.5">{s.kicker}</p>
            <p className="text-sm text-slate-300 mt-2">{s.text}</p>
            <p className="text-xs text-amber-300/80 mt-2 italic">{s.hint}</p>
          </div>
        ) : s.maestro ? (
          <div className="mt-1">
            <p className="font-display text-lg font-extrabold tracking-tight inline-flex items-center gap-2" data-testid="demo-tour-step-title">
              {Icon && <Icon className="h-4.5 w-4.5 text-amber-400" style={{ height: 18, width: 18 }} />} {s.title}
            </p>
            <p className="text-sm italic text-amber-300 mt-0.5" data-testid="demo-tour-quote">“{s.quote}”</p>
            {s.items && (
              <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="demo-tour-maestro-items">
                {s.items.map((it) => (
                  <span key={it} className="px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-600/40 text-amber-200 text-xs font-medium">{it}</span>
                ))}
              </div>
            )}
            {s.text && <p className="text-sm text-slate-300 mt-2">{s.text}</p>}
          </div>
        ) : (
          <>
            <p className="font-display text-lg font-extrabold tracking-tight mt-1" data-testid="demo-tour-step-title">{s.title}</p>
            <p className="text-sm text-slate-300 mt-1">{s.text}</p>
          </>
        )}

        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-1.5">
            {steps.map((st, i) => (
              <button key={i} onClick={() => go(i)} className={`h-1.5 rounded-full transition-all ${i === step ? `w-5 ${s.maestro ? "bg-amber-400" : "bg-sky-400"}` : st.maestro ? "w-1.5 bg-amber-800 hover:bg-amber-600" : "w-1.5 bg-slate-600 hover:bg-slate-500"}`} data-testid={`demo-tour-dot-${i}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button size="sm" variant="ghost" className="gap-1 text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => go(step - 1)} data-testid="demo-tour-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
            )}
            {last ? (
              <Button size="sm" className="gap-1 bg-sky-500 hover:bg-sky-600 text-white" onClick={() => setActive(false)} data-testid="demo-tour-finish">
                <Flag className="h-3.5 w-3.5" /> Finish
              </Button>
            ) : (
              <Button size="sm" className={`gap-1 text-white ${s.maestro ? "bg-amber-500 hover:bg-amber-600" : "bg-sky-500 hover:bg-sky-600"}`} onClick={() => go(step + 1)} data-testid="demo-tour-next">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
